/**
 * Read-only highlighted source for a code File. Used by the desktop
 * code-preview sidebar and the full-screen artifact viewer.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Linking, Platform, Pressable, ScrollView, StyleSheet, Text as RNText, View } from 'react-native';
import { Highlight } from 'prism-react-renderer';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { filesApi } from '@/lib/api';
import { createFileOpenGuard } from '@/lib/fileOpenSession';
import { openFileExternally } from '@/constants/utils/files';
import DocumentViewer from '@/components/Files/DocumentViewer';
import HtmlArtifactPreview from '@/components/Files/HtmlArtifactPreview';
import WorkSitePublish from '@/components/Coworker/WorkSitePublish';
import ArtifactTooltip from '@/components/Files/ArtifactTooltip';
import {
  HIGHLIGHT_CHUNK_LINES,
  codeFileIsTooLarge,
  injectHtmlPreviewCsp,
  isHtmlCodeFile,
  isMarkdownCodeFile,
  languageFromCodeFile,
  looksLikeBinaryText,
} from '@/constants/utils/code_file';
import { codeHighlightTheme, prismLanguage } from '@/constants/utils/code_highlight';
import { codeChangeStats, lineDiffParts } from '@/constants/utils/code_diff';

let NativeWebView = null;
if (Platform.OS !== 'web') {
  try {
    const webViewModule = require('react-native-webview');
    NativeWebView = Platform.OS === 'macos' ? require('@/components/Files/MacArtifactWebView').default
      : webViewModule.WebView || webViewModule.default;
  } catch {
    NativeWebView = null;
  }
}

async function loadCodeText(file, isRequestCurrent) {
  if (!isRequestCurrent()) return null;
  const fileId = file?.id || file?._id;
  if (fileId) {
    try {
      const payload = await filesApi.fetchCodeText(fileId, { isRequestCurrent });
      if (!isRequestCurrent()) return null;
      if (payload?.text != null) return payload;
    } catch { /* fall through to the CDN */ }
  }
  // A failed authenticated read may settle after the viewer closes or the
  // account changes. Only the current preview may start a fallback download.
  if (!isRequestCurrent()) return null;
  const url = file?.cdnPath || file?.url || file?.uri;
  if (url) {
    const response = await fetch(url);
    if (!isRequestCurrent()) return null;
    if (response.ok) {
      const text = await response.text();
      return isRequestCurrent() ? { text, originalText: null } : null;
    }
  }
  throw new Error('missing-file');
}

function sourceChunks(source) {
  const lines = String(source || '').split('\n');
  const chunks = [];
  for (let start = 0; start < lines.length; start += HIGHLIGHT_CHUNK_LINES) {
    chunks.push({
      start,
      code: lines.slice(start, start + HIGHLIGHT_CHUNK_LINES).join('\n'),
    });
  }
  return chunks.length ? chunks : [{ start: 0, code: '' }];
}

class HighlightGuard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) return this.props.fallback;
    return this.props.children;
  }
}

function PlainSource({ source, gutterColor, styles, colors }) {
  const lines = String(source || '').split('\n');
  return (
    <View>
      {lines.map((line, index) => (
        <View key={index} style={styles.line}>
          <RNText style={[styles.gutter, { color: gutterColor }]}>{index + 1}</RNText>
          <RNText style={[styles.code, { color: colors?.text_color_primary || '#e6e6e6' }]}>
            {line || ' '}
          </RNText>
        </View>
      ))}
    </View>
  );
}

function HighlightedSource({ source, language, dark, gutterColor, styles, colors }) {
  const prismLang = prismLanguage(language);
  const highlightTheme = codeHighlightTheme(dark);
  const chunks = sourceChunks(source);
  const fallback = (
    <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} horizontal={false}>
      <ScrollView horizontal showsHorizontalScrollIndicator>
        <PlainSource source={source} gutterColor={gutterColor} styles={styles} colors={colors} />
      </ScrollView>
    </ScrollView>
  );

  return (
    <HighlightGuard fallback={fallback}>
    <ScrollView
      style={[styles.scroll, { backgroundColor: highlightTheme.plain.backgroundColor || 'transparent' }]}
      contentContainerStyle={styles.scrollContent}
      horizontal={false}
    >
      <ScrollView horizontal showsHorizontalScrollIndicator>
        <View>
          {chunks.map((chunk) => (
            <Highlight
              key={chunk.start}
              theme={highlightTheme}
              code={chunk.code}
              language={prismLang}
            >
              {({ tokens, getLineProps, getTokenProps, style }) => (
                Platform.OS === 'web' ? (
                  <pre
                    style={{
                      ...style,
                      margin: 0,
                      padding: 0,
                      background: 'transparent',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: 13,
                      lineHeight: '20px',
                    }}
                  >
                    {tokens.map((line, index) => {
                      const lineProps = getLineProps({ line });
                      return (
                        <div
                          key={chunk.start + index}
                          {...lineProps}
                          style={{
                            ...lineProps.style,
                            display: 'flex',
                            alignItems: 'flex-start',
                            minHeight: 20,
                          }}
                        >
                          <span
                            style={{
                              width: 52,
                              paddingRight: 12,
                              textAlign: 'right',
                              color: gutterColor,
                              userSelect: 'none',
                              flexShrink: 0,
                            }}
                          >
                            {chunk.start + index + 1}
                          </span>
                          <span style={{ whiteSpace: 'pre' }}>
                            {line.map((token, key) => {
                              const props = getTokenProps({ token });
                              return (
                                <span key={key} className={props.className} style={props.style}>
                                  {props.children}
                                </span>
                              );
                            })}
                            {line.length === 0 ? '\n' : null}
                          </span>
                        </div>
                      );
                    })}
                  </pre>
                ) : (
                  <View>
                    {tokens.map((line, index) => (
                      <View key={chunk.start + index} style={styles.line}>
                        <RNText style={[styles.gutter, { color: gutterColor }]}>
                          {chunk.start + index + 1}
                        </RNText>
                        <RNText style={[styles.code, { color: highlightTheme.plain.color }]}>
                          {line.map((token, key) => {
                            const props = getTokenProps({ token });
                            return (
                              <RNText key={key} style={[styles.code, props.style]}>
                                {props.children}
                              </RNText>
                            );
                          })}
                          {line.length === 0 ? ' ' : ''}
                        </RNText>
                      </View>
                    ))}
                  </View>
                )
              )}
            </Highlight>
          ))}
        </View>
      </ScrollView>
    </ScrollView>
    </HighlightGuard>
  );
}

function flattenDiffRows(before, after) {
  return lineDiffParts(before, after).flatMap((part) => {
    const kind = part.added ? 'added' : part.removed ? 'removed' : 'same';
    const raw = String(part.value || '');
    if (!raw) return [];
    const lines = raw.replace(/\n$/, '').split('\n');
    return lines.map((text) => ({ kind, text }));
  });
}

function StatusCard({ icon, title, body, actionLabel, onAction, onRetry, colors, styles }) {
  return (
    <View style={styles.empty}>
      <Icon name={icon} size={28} color={colors.text_color_secondary} />
      <Text weight="semibold" align="center">{title}</Text>
      {!!body && <Text size="s" color="secondary" align="center">{body}</Text>}
      {onRetry && <Pressable accessibilityRole="button" accessibilityLabel="Retry"
        onPress={onRetry} style={styles.downloadButton}>
        <Icon name="rotate-right" size={13} color={colors.text_color_primary} />
        <Text size="s">Retry</Text>
      </Pressable>}
      {onAction ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={actionLabel}
          onPress={onAction}
          style={styles.downloadButton}
        >
          <Icon name="arrow-down-to-line" size={13} color={colors.text_color_primary} />
          <Text size="s">{actionLabel}</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

export default function CodeViewer({ file, previewTitle, previewActions, onFileSaved }) {
  const { colors, theme } = useTheme();
  const dark = isDarkTheme(theme);
  const language = languageFromCodeFile(file);
  const tooLarge = codeFileIsTooLarge(file);
  const seededStats = codeChangeStats(file);
  const hasHtmlPreview = Boolean(
    isHtmlCodeFile(file) && (Platform.OS === 'web' || NativeWebView)
  );
  const hasMarkdownPreview = isMarkdownCodeFile(file);
  const hasRichPreview = hasHtmlPreview || hasMarkdownPreview;
  const [text, setText] = useState('');
  const [originalText, setOriginalText] = useState(null);
  const [tab, setTab] = useState(seededStats ? 'changes' : (hasRichPreview ? 'preview' : 'file'));
  const [status, setStatus] = useState(tooLarge ? 'large' : 'loading');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (tooLarge) {
      setText('');
      setOriginalText(null);
      setStatus('large');
      return undefined;
    }
    let cancelled = false;
    const isRequestCurrent = createFileOpenGuard(() => !cancelled);
    setStatus('loading');
    loadCodeText(file, isRequestCurrent)
      .then((payload) => {
        if (!isRequestCurrent()) return;
        const body = typeof payload === 'string' ? payload : (payload?.text || '');
        const prior = typeof payload === 'string' ? null : (payload?.originalText || null);
        if (looksLikeBinaryText(body)) {
          setText('');
          setOriginalText(null);
          setStatus('binary');
          return;
        }
        setText(body);
        setOriginalText(prior);
        setTab(prior != null ? 'changes' : (hasRichPreview ? 'preview' : 'file'));
        setStatus(body ? 'ready' : 'empty');
      })
      .catch(() => {
        if (isRequestCurrent()) {
          setText('');
          setOriginalText(null);
          setStatus('error');
        }
      });
    return () => { cancelled = true; };
  }, [file, hasRichPreview, tooLarge, retryKey]);

  const markdownPreviewFile = useMemo(() => ({
    ...file,
    parameters: {
      ...file?.parameters,
      documentSourceMarkdown: text,
    },
  }), [file, text]);

  const previewHtml = useMemo(() => {
    if (!hasHtmlPreview || !text) return '';
    return injectHtmlPreviewCsp(text);
  }, [hasHtmlPreview, text]);

  const styles = useMemo(() => StyleSheet.create({
    container: { flex: 1, minWidth: 0, minHeight: 0 },
    empty: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      padding: 32,
    },
    downloadButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginTop: 6,
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 8,
      backgroundColor: colors.solid_color_secondary,
    },
    scroll: { flex: 1 },
    scrollContent: { paddingVertical: 12, paddingRight: 16, minWidth: '100%' },
    line: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      minHeight: 20,
    },
    gutter: {
      width: 52,
      paddingRight: 12,
      textAlign: 'right',
      fontVariant: ['tabular-nums'],
    },
    code: {
      flexShrink: 1,
      fontFamily: Platform.select({
        ios: 'Menlo',
        macos: 'Menlo',
        android: 'monospace',
        default: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      }),
      fontSize: 13,
      lineHeight: 20,
    },
    tabs: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },
    tab: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 8,
      ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
    },
    tabActive: {
      backgroundColor: colors.solid_color_secondary,
    },
    htmlPreview: {
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      backgroundColor: '#FFFFFF',
    },
    sandboxBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 5,
      backgroundColor: colors.solid_color_block,
      borderBottomWidth: 1,
      borderBottomColor: colors.border_color_secondary,
    },
    diffLine: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      minHeight: 20,
    },
    diffMarker: {
      width: 18,
      textAlign: 'center',
      flexShrink: 0,
    },
  }), [colors]);

  const withFileHeader = content => previewTitle || previewActions ? (
    <View style={styles.container}>
      <View style={{ flexDirection: 'row', alignItems: 'center', minHeight: 42, paddingHorizontal: 8, gap: 6, borderBottomWidth: 1, borderBottomColor: colors.border_color_secondary }}>
        {previewTitle}<View style={{ flex: 1 }} />{previewActions}
      </View>
      {content}
    </View>
  ) : content;

  if (status === 'loading') {
    return withFileHeader(
      <View style={styles.empty}>
        <ActivityIndicator color={colors.text_color_secondary} />
        <Text size="s" color="secondary">Opening file…</Text>
      </View>
    );
  }

  if (status === 'large') {
    return withFileHeader(
      <StatusCard
        icon="file-code"
        title="This file is too large to preview"
        body="Download it to open it in your editor."
        actionLabel="Download"
        onAction={() => openFileExternally(file)}
        colors={colors}
        styles={styles}
      />
    );
  }

  if (status === 'binary') {
    return withFileHeader(
      <StatusCard
        icon="file"
        title="This does not look like text"
        body="Download the file if you still want to open it."
        actionLabel="Download"
        onAction={() => openFileExternally(file)}
        colors={colors}
        styles={styles}
      />
    );
  }

  if (status === 'error' || status === 'empty') {
    return withFileHeader(
      <StatusCard
        icon="file-code"
        title={status === 'empty' ? 'This file is empty' : 'Could not open this file'}
        body="You can still download the original."
        onRetry={status === 'error' ? () => setRetryKey(value => value + 1) : undefined}
        actionLabel="Download"
        onAction={() => openFileExternally(file)}
        colors={colors}
        styles={styles}
      />
    );
  }

  const source = text.replace(/\n$/, '');
  const gutterColor = dark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)';
  const showChanges = originalText != null;
  const viewingChanges = showChanges && tab === 'changes';
  const viewingPreview = hasRichPreview && tab === 'preview';
  const diffRows = showChanges ? flattenDiffRows(originalText, text) : [];
  const stats = seededStats || (showChanges ? {
    added: diffRows.filter((row) => row.kind === 'added').length,
    removed: diffRows.filter((row) => row.kind === 'removed').length,
  } : null);
  const addedBg = dark ? 'rgba(34, 197, 94, 0.16)' : 'rgba(34, 197, 94, 0.12)';
  const removedBg = dark ? 'rgba(239, 68, 68, 0.16)' : 'rgba(239, 68, 68, 0.12)';

  const sourceView = (
    <HighlightedSource
      source={source}
      language={language}
      dark={dark}
      gutterColor={gutterColor}
      styles={styles}
      colors={colors}
    />
  );

  const renderTabs = onInteract => showChanges || hasRichPreview ? (
    <View style={[styles.tabs, hasHtmlPreview && Platform.OS === 'web' && { paddingHorizontal: 0, paddingVertical: 0, borderBottomWidth: 0, backgroundColor: 'transparent', flexShrink: 0 }]} accessibilityRole="tablist">
      <ArtifactTooltip title="View source" description="Read the original source code without restarting the preview."><Pressable
        accessibilityRole="tab"
        accessibilityState={{ selected: tab === 'file' }}
        accessibilityLabel={hasMarkdownPreview ? 'Markdown source' : 'Code'}
        onPress={() => setTab('file')}
        style={[styles.tab, tab === 'file' ? styles.tabActive : null]}
      >
        <Text size="xs" weight={tab === 'file' ? 'semibold' : 'regular'}>{hasMarkdownPreview ? 'Source' : 'Code'}</Text>
      </Pressable></ArtifactTooltip>
      {showChanges && (
        <ArtifactTooltip title="Compare changes" description="See what was added or removed compared with the previous version."><Pressable accessibilityRole="tab" accessibilityState={{ selected: tab === 'changes' }} accessibilityLabel="Changes" onPress={() => setTab('changes')} style={[styles.tab, tab === 'changes' ? styles.tabActive : null]}>
          <Text size="xs" weight={tab === 'changes' ? 'semibold' : 'regular'}>Changes</Text>
          {stats && <><Text size="xs" style={{ color: '#22C55E' }}>+{stats.added}</Text><Text size="xs" style={{ color: '#EF4444' }}>−{stats.removed}</Text></>}
        </Pressable></ArtifactTooltip>
      )}
      {hasRichPreview && (
        <ArtifactTooltip title="Use the preview" description="Run the page normally. Click here to leave selection mode and use its buttons and controls."><Pressable
          accessibilityRole="tab"
          accessibilityState={{ selected: tab === 'preview' }}
          accessibilityLabel={hasMarkdownPreview ? 'Preview document' : 'Preview app'}
          onPress={() => { setTab('preview'); onInteract?.(); }}
          style={[styles.tab, tab === 'preview' ? styles.tabActive : null]}
        >
          <Icon name={hasMarkdownPreview ? 'file-lines' : 'play'} size={11} color={colors.text_color_primary} />
          <Text size="xs" weight={tab === 'preview' ? 'semibold' : 'regular'}>Preview</Text>
        </Pressable></ArtifactTooltip>
      )}
    </View>
  ) : null;

  const codeContent = viewingChanges ? (
<ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          horizontal={false}
        >
          <ScrollView horizontal showsHorizontalScrollIndicator>
            <View>
              {diffRows.map((row, index) => {
                const bg = row.kind === 'added' ? addedBg : row.kind === 'removed' ? removedBg : 'transparent';
                const marker = row.kind === 'added' ? '+' : row.kind === 'removed' ? '−' : ' ';
                const markerColor = row.kind === 'added' ? '#22C55E' : row.kind === 'removed' ? '#EF4444' : gutterColor;
                return (
                  <View key={`${row.kind}-${index}`} style={[styles.diffLine, { backgroundColor: bg }]}>
                    <RNText style={[styles.code, styles.diffMarker, { color: markerColor }]}>{marker}</RNText>
                    <RNText style={[styles.code, { color: colors.text_color_primary }]}>
                      {row.text || ' '}
                    </RNText>
                  </View>
                );
              })}
            </View>
          </ScrollView>
        </ScrollView>
  ) : sourceView;

  const publishControl = hasHtmlPreview && file?.canPublishSite !== false && (file?.id || file?._id || file?.parameters?.workTaskId) ? (
    <ScrollView style={{ flexGrow: 0, maxHeight: '55%' }} contentContainerStyle={{ padding: 12 }} keyboardShouldPersistTaps="handled">
      <WorkSitePublish taskId={file?.parameters?.workTaskId} file={file} />
    </ScrollView>
  ) : null;
  if (hasHtmlPreview && Platform.OS === 'web') {
    return <View style={styles.container}><HtmlArtifactPreview onFileSaved={onFileSaved} file={file} html={text} active={viewingPreview} renderTabs={renderTabs} sourceContent={codeContent} onShowPreview={() => setTab('preview')} titleContent={previewTitle} fileActions={previewActions} />{publishControl}</View>;
  }

  return (
    <View style={styles.container}>
      {renderTabs()}
      {(viewingPreview || (Platform.OS === 'macos' && hasHtmlPreview)) ? (
        hasMarkdownPreview ? (
          <DocumentViewer file={markdownPreviewFile} />
        ) : (
          <View style={[styles.htmlPreview, !viewingPreview && { display: 'none' }]}
            accessibilityElementsHidden={!viewingPreview}>
            <View style={styles.sandboxBanner}>
              <Icon name="shield-halved" size={11} color={colors.text_color_secondary} />
              <Text size="xxs" color="secondary" numberOfLines={1}>
                Sandboxed preview • Do not enter passwords or sensitive personal info
              </Text>
            </View>
              <NativeWebView
                source={{ html: previewHtml }}
                style={styles.htmlPreview}
                originWhitelist={['about:blank', 'data:*', 'https://*', 'http://*']}
                javaScriptEnabled
                allowsInlineMediaPlayback
                setSupportMultipleWindows={false}
                onShouldStartLoadWithRequest={(request) => {
                  const url = String(request?.url || '');
                  if (!url || url === 'about:blank' || url.startsWith('data:text/html')) return true;
                  if (/^https?:\/\//i.test(url)) Linking.openURL(url).catch(() => {});
                  return false;
                }}
              />
          </View>
        )

      ) : null}
      {!viewingPreview ? codeContent : null}
      {publishControl}
    </View>
  );
}
